import moment from 'moment/moment';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import type { AsyncStatus } from '../../../@types/AsyncStatus';
import type { RootState } from '../../../store/store';
import { JWTAuthorization, JWTHeader } from '../../../utils/JWTHeader';
import { ROUTES } from '../../../utils/routes';
import tailwindConfig from '../../../utils/tailwindColors';
import Badge from '../../common/Badge';
import { AdapterMoment, DatePicker, LocalizationProvider } from '../../common/DatePickerCompat';
import MUITable from '../../common/MUITable';
import { showToast } from '../../common/Notification/ToastrNotif.module';
import Popup from '../../common/Popup';
import DismissibleModuleTutorialPopup from '../../common/Tutorials/DismissibleModuleTutorialPopup';
import WarningValidationPending from '../../common/WarningValidationPending';

type Filters = {
  agMois: 'all' | 'current' | 'next';
  dateState:
    | 'all'
    | 'with-last'           // Avec dernière date AG
    | 'without-last'        // Sans dernière date AG
    | 'with-planned'        // Avec date AG prévue
    | 'without-planned'     // Sans date AG prévue
    | 'none'                // Sans aucune date AG
    | 'both';               // Avec date AG passé et à venir
  search: string;
};

type Row = {
  id: number;
  remoteId: string;
  name: string;
  address: string;
  cp: string;
  city: string;
  nbLots: number | null;
  lastAg: string | null; // yyyy-MM-dd
  plannedAg: string | null; // yyyy-MM-dd
  meetingDates?: string[]; // normalized yyyy-MM-dd list (0..2: last + planned)
  nextAgForecast: string | null; // MMM YYYY
  qualification: 'qualified' | 'not-qualified';
};

type BlurDatePickerProps = {
  value: string | null;
  onSave: (iso: string) => void;
  minDate?: moment.Moment;
  maxDate?: moment.Moment;
  referenceDate?: moment.Moment;
  errorMessageMin?: string;
  errorMessageMax?: string;
};

function BlurDatePicker({ value, onSave, minDate, maxDate, referenceDate, errorMessageMin, errorMessageMax }: BlurDatePickerProps) {
  const [localValue, setLocalValue] = useState<moment.Moment | null>(value ? moment(value, 'YYYY-MM-DD') : null);
  const savedValueRef = useRef<string | null>(value);
  const isPickerOpenRef = useRef(false);

  useEffect(() => {
    setLocalValue(value ? moment(value, 'YYYY-MM-DD') : null);
    savedValueRef.current = value;
  }, [value]);

  const isReasonableDate = (m: moment.Moment | null): boolean => {
    if (!m || !m.isValid()) { return false; }
    const year = m.year();
    return year >= 1900 && year <= 2100;
  };

  const revert = useCallback(() => {
    setLocalValue(savedValueRef.current ? moment(savedValueRef.current, 'YYYY-MM-DD') : null);
  }, []);

  const doSave = useCallback((m: moment.Moment | null) => {
    if (!isReasonableDate(m)) { return; }
    const iso = m!.format('YYYY-MM-DD');
    if (iso === savedValueRef.current) { return; }
    if (minDate && m!.isBefore(minDate, 'day')) {
      showToast('error', errorMessageMin || 'La date sélectionnée est trop ancienne');
      revert();
      return;
    }
    if (maxDate && m!.isAfter(maxDate, 'day')) {
      showToast('error', errorMessageMax || 'La date sélectionnée est trop récente');
      revert();
      return;
    }
    onSave(iso);
  }, [onSave, minDate, maxDate, errorMessageMin, errorMessageMax, revert]);

  return (
    <DatePicker
      value={localValue}
      onChange={(newValue) => setLocalValue(newValue)}
      onOpen={() => { isPickerOpenRef.current = true; }}
      onClose={() => { isPickerOpenRef.current = false; }}
      onAccept={(newValue) => doSave(newValue)}
      format="DD/MM/YYYY"
      views={['day']}
      slotProps={{
        textField: {
          size: 'small',
          onBlur: () => {
            if (!isPickerOpenRef.current) {
              doSave(localValue);
            }
          },
        },
      }}
      {...(minDate ? { minDate } : {})}
      {...(maxDate ? { maxDate } : {})}
      {...(referenceDate ? { referenceDate } : {})}
    />
  );
}

function GeneralAssemblyManager() {
  const jwt = useSelector((state: RootState) => state.auth.token);
  const company = useSelector((state: RootState) => state.auth.company);
  const navigate = useNavigate();
  const [status, setStatus] = useState<AsyncStatus>('IDLE');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [rows, setRows] = useState<Row[]>([]);
  const [filtered, setFiltered] = useState<Row[]>([]);
  const [filters, setFilters] = useState<Filters>({ agMois: 'all', dateState: 'all', search: '' });
  const [infoModalOpen, setInfoModalOpen] = useState<boolean>(false);

  const computeNextAgForecast = (planned: string | null, lastAg: string | null): string | null => {
    // If no lastAg is set, we cannot provide a forecast
    if (!lastAg) {
      return 'Prevision impossible';
    }
    
    // Use the last AG date to compute the forecast (next AG should be ~1 year after last AG)
    try {
      const lastAgDate = new Date(lastAg);
      if (isNaN(lastAgDate.getTime())) {
        return 'Prevision impossible';
      }
      
      // Add 1 year to the last AG date
      const forecastDate = new Date(lastAgDate);
      forecastDate.setFullYear(forecastDate.getFullYear() + 1);
      
      return moment(forecastDate).format('MMM YYYY');
    } catch (error) {
      console.error('Error computing AG forecast:', error);
      return 'Prevision impossible';
    }
  };

  const fetchData = () => {
    if (!company?.id || !jwt) { return; }
    setStatus('PENDING');
    fetch(`/api/companies/${company.id.toString()}/copros/meeting`, JWTHeader(jwt))
      .then(async(r) => { if (!r.ok) { throw new Error('fetch'); } return r.json(); })
      .then((data) => {
        const mapped: Row[] = (data as Array<{ id: number; remoteId: string; name: string; address: string; cp: string; city: string; batchSize?: number; meetingDates?: string[] }>).map((c) => {
          const arr: string[] = Array.isArray(c.meetingDates) ? c.meetingDates
            .map((d: string) => {
              try { const dt = new Date(d); return isNaN(dt.getTime()) ? '' : dt.toISOString().split('T')[0]; } catch { return ''; }
            })
            .filter(Boolean)
            .sort()
          : [];
          const today = new Date(); today.setHours(0, 0, 0, 0);
          let lastAg: string | null = null;
          for (let i = arr.length - 1; i >= 0; i -= 1) {
            const d = new Date(arr[i]);
            if (!isNaN(d.getTime()) && d.getTime() <= today.getTime()) { lastAg = arr[i]; break; }
          }
          const plannedAg = (() => {
            const future = arr
              .map((iso) => new Date(iso))
              .filter((d) => !isNaN(d.getTime()) && d.getTime() > today.getTime())
              .sort((a, b) => a.getTime() - b.getTime());
            return future.length > 0 ? future[0].toISOString().split('T')[0] : null;
          })();
          const nextAgForecast = computeNextAgForecast(plannedAg, lastAg);
          const qualification: 'qualified' | 'not-qualified' = plannedAg ? 'qualified' : 'not-qualified';
          return {
            id: c.id,
            remoteId: c.remoteId,
            name: c.name,
            address: c.address,
            cp: c.cp,
            city: c.city,
            nbLots: c.batchSize ?? null,
            lastAg,
            plannedAg,
            meetingDates: arr,
            nextAgForecast,
            qualification,
          };
        });
        setRows(mapped);
        setStatus('SUCCESS');
      })
      .catch(() => {
        setErrorMsg('Une erreur est survenue lors du chargement des données');
        setStatus('ERROR');
      });
  };

  useEffect(() => { fetchData(); }, [company?.id, jwt]);

  useEffect(() => {
    const term = filters.search.trim().toLowerCase();
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    const nextMonth = currentMonth === 11 ? 0 : currentMonth + 1;
    const nextYear = currentMonth === 11 ? currentYear + 1 : currentYear;

    const res = rows.filter((r) => {
      let show = true;
      if (term) {
        const hay = `${r.remoteId} ${r.name} ${r.address} ${r.cp} ${r.city}`.toLowerCase();
        if (!hay.includes(term)) { show = false; }
      }
      if (show && filters.agMois !== 'all') {
        const allDates: string[] = Array.isArray(r.meetingDates) && r.meetingDates.length > 0
          ? r.meetingDates
          : (r.plannedAg ? [r.plannedAg] : []);
        const matches = allDates.some((iso) => {
          const d = new Date(iso);
          if (isNaN(d.getTime())) { return false; }
          const m = d.getMonth(); const y = d.getFullYear();
          if (filters.agMois === 'current') { return m === currentMonth && y === currentYear; }
          if (filters.agMois === 'next') { return m === nextMonth && y === nextYear; }
          return false;
        });
        if (!matches) { show = false; }
      }
      if (show && filters.dateState !== 'all') {
        const hasPlanned = !!r.plannedAg;          // date AG prévue (à venir)
        const hasLast = !!r.lastAg;                // dernière date d'AG (passée)

        if (filters.dateState === 'with-last' && !hasLast) { show = false; }
        if (filters.dateState === 'without-last' && hasLast) { show = false; }
        if (filters.dateState === 'with-planned' && !hasPlanned) { show = false; }
        if (filters.dateState === 'without-planned' && hasPlanned) { show = false; }
        if (filters.dateState === 'none' && (hasPlanned || hasLast)) { show = false; }
        if (filters.dateState === 'both' && !(hasPlanned && hasLast)) { show = false; }
      }
      return show;
    });
    setFiltered(res);
  }, [rows, filters]);

  const onChangePlanned = (id: number, iso: string) => {
    if (!iso) { return; }

    const currentRow = rows.find(r => r.id === id);
    const lastAg = currentRow?.lastAg ?? null;
    const newDates = lastAg ? [lastAg, iso] : [iso];

    fetch(`/api/copros/${id}/general-meeting-dates`, {
      method: 'PUT',
      headers: { Authorization: JWTAuthorization(jwt), 'Content-Type': 'application/json' },
      body: JSON.stringify({ generalMeetingDates: newDates }),
    })
      .then(async(r) => { if (!r.ok) { throw new Error('update'); } return r.json(); })
      .then(() => {
        showToast('success', `Copro ${currentRow?.remoteId} - ${currentRow?.name} : Date AG prévue enregistrée au ${moment(iso).format('DD/MM/YYYY')}`);
        setRows((prev) => prev.map((r) => {
          if (r.id !== id) { return r; }
          const meetingDates = [...newDates].sort();
          return {
            ...r,
            plannedAg: iso,
            meetingDates,
            nextAgForecast: computeNextAgForecast(iso, r.lastAg),
            qualification: 'qualified',
          };
        }));
      })
      .catch(() => setErrorMsg('Impossible de mettre à jour la date d\'AG'));
  };

  const onChangeLastAg = (id: number, iso: string) => {
    if (!iso) { return; }

    const currentRow = rows.find(r => r.id === id);
    const planned = currentRow?.plannedAg ?? null;
    const newDates = planned ? [iso, planned] : [iso];

    fetch(`/api/copros/${id}/general-meeting-dates`, {
      method: 'PUT',
      headers: { Authorization: JWTAuthorization(jwt), 'Content-Type': 'application/json' },
      body: JSON.stringify({ generalMeetingDates: newDates }),
    })
      .then(async(r) => { if (!r.ok) { throw new Error('update'); } return r.json(); })
      .then(() => {
        showToast('success', `Copro ${currentRow?.remoteId} - ${currentRow?.name} : Dernière AG enregistrée au ${moment(iso).format('DD/MM/YYYY')}`);
        setRows((prev) => prev.map((r) => {
          if (r.id !== id) { return r; }
          const meetingDates = [...newDates].sort();
          return { ...r, lastAg: iso, meetingDates, nextAgForecast: computeNextAgForecast(r.plannedAg, iso) };
        }));
      })
      .catch(() => setErrorMsg('Impossible de mettre à jour la date de dernière AG'));
  };

  const layoutFilters = (
    <div className="mb-4 rounded-lg border border-slate-200 bg-gradient-to-r from-slate-100 to-slate-50 p-4">
      <div className="space-y-4">
        {/* Search and filters layout */}
        <div className="space-y-3 md:grid md:grid-cols-2 md:gap-4">
          <div className="space-y-2 md:flex md:items-center md:space-y-0 md:space-x-2">
            <label className="block text-sm font-medium text-slate-700 md:whitespace-nowrap">AG du mois :</label>
            <select 
              className="block w-full rounded-md border border-slate-300 p-2 text-sm md:w-auto"
              value={filters.agMois} 
              onChange={(e) => setFilters({ ...filters, agMois: e.target.value as Filters['agMois'] })}
            >
              <option value="all">Tous</option>
              <option value="current">AG de ce mois</option>
              <option value="next">AG mois prochain</option>
            </select>
          </div>
          <div className="space-y-2 md:flex md:items-center md:space-y-0 md:space-x-2">
            <label className="block text-sm font-medium text-slate-700 md:whitespace-nowrap">État des dates :</label>
            <select 
              className="block w-full rounded-md border border-slate-300 p-2 text-sm md:w-auto"
              value={filters.dateState} 
              onChange={(e) => setFilters({ ...filters, dateState: e.target.value as Filters['dateState'] })}
            >
              <option value="all">Tous</option>
              <option value="with-last">Avec dernière date AG</option>
              <option value="without-last">Sans dernière date AG</option>
              <option value="with-planned">Avec date AG prévue</option>
              <option value="without-planned">Sans date AG prévue</option>
              <option value="none">Sans aucune date AG</option>
              <option value="both">Avec date AG passé et à venir</option>
            </select>
          </div>
        </div>

        {/* Search and reset button group */}
        <div className="space-y-3 md:flex md:items-center md:space-y-0 md:space-x-4">
          <div className="space-y-2 md:flex md:flex-1 md:items-center md:space-y-0 md:space-x-2">
            <label className="block text-sm font-medium text-slate-700 md:whitespace-nowrap"><span role="img" aria-label="magnifying glass">🔍</span> Rechercher :</label>
            <input
              className="block w-full rounded-md border border-slate-300 p-2 text-sm"
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              placeholder="Nom, adresse..."
            />
          </div>
          <div className="flex justify-end gap-3 md:justify-start">
            <button 
              onClick={() => setFilters({ agMois: 'all', dateState: 'all', search: '' })}
              className="rounded-md bg-blue-600 px-3 py-2 text-sm text-white hover:bg-blue-700"
            >
              ↻ Réinitialiser
            </button>
            <button className="rounded-md bg-green-600 px-3 py-2 text-sm text-white hover:bg-green-700">
              <span role="img" aria-label="chart">📊</span> Export Complet
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const headers = [
    { title: 'N° COPRO', class: '' },
    { title: 'NOM IMMEUBLE', class: '' },
    { title: 'ADRESSE', class: '' },
    { title: 'NB LOTS', class: '', sortable: true },
    { title: 'DERNIERE AG', class: '', sortable: true },
    { title: 'PREVISION PROCHAINE AG', class: '', sortable: true },
    { title: 'DATE AG PRÉVUE', class: '', sortable: true },
    { title: 'ACTIONS', class: '' },
  ];

  const tableData = filtered.map((r) => {
    const address = `${r.address}\n${r.cp} ${r.city}`;
    
    return {
      'N° COPRO': <span className="text-xs font-semibold text-slate-900">{r.remoteId}</span>,
      'NOM IMMEUBLE': <span className="text-xs font-semibold text-indigo-800">{r.name}</span>,
      'ADRESSE': <span className="text-left text-xs leading-tight whitespace-pre">{address}</span>,
      'NB LOTS': r.nbLots ?? '-',
      'DERNIERE AG': (
        <BlurDatePicker
          value={r.lastAg}
          onSave={(iso) => onChangeLastAg(r.id, iso)}
          maxDate={moment().endOf('day')}
          errorMessageMax="La date de dernière AG ne peut pas être dans le futur"
          referenceDate={(() => {
            if (r.lastAg) { return undefined as unknown as moment.Moment; }
            if (r.plannedAg) {
              const m = moment(r.plannedAg, 'YYYY-MM-DD').subtract(1, 'year');
              return m.isValid() ? m : (undefined as unknown as moment.Moment);
            }
            return undefined as unknown as moment.Moment;
          })()}
        />
      ),
      'PREVISION PROCHAINE AG': (
        <span className={`rounded px-2 py-1 text-xs font-semibold ${r.nextAgForecast === 'Prevision impossible' ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700'}`}>
          {r.nextAgForecast ?? '-'}
        </span>
      ),
      'DATE AG PRÉVUE': (
        <BlurDatePicker
          value={r.plannedAg}
          onSave={(iso) => onChangePlanned(r.id, iso)}
          minDate={moment().startOf('day')}
          errorMessageMin="La date d'AG prévue doit être dans le futur"
          referenceDate={(() => {
            if (r.plannedAg) { return undefined as unknown as moment.Moment; }
            if (!r.nextAgForecast || r.nextAgForecast === 'Prevision impossible') { return undefined as unknown as moment.Moment; }
            const m = moment(r.nextAgForecast, 'MMM YYYY');
            return m.isValid() ? m : (undefined as unknown as moment.Moment);
          })()}
        />
      ),
      'ACTIONS': (
        <button 
          className="rounded bg-indigo-600 px-2 py-1 text-xs text-white hover:bg-indigo-700" 
          onClick={() => navigate(ROUTES.COMMUNICATION_REFERENT_CAMPAIGNS)}
        >
          Éditer Document AG
        </button>
      ),
    };
  });

  return (
    <div>
      <LocalizationProvider dateAdapter={AdapterMoment}>
      <div className="mb-8 rounded-lg bg-white p-4 shadow-md">
        <WarningValidationPending />
        <DismissibleModuleTutorialPopup tutorial="general-assembly-manager" />
        {/* Top KPI cards */}
        {(() => {
          const totalCopros = rows.length;
          const now = new Date();
          const currentMonth = now.getMonth();
          const currentYear = now.getFullYear();
          const monthNamesFr = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
          const monthLabel = monthNamesFr[currentMonth] || '';
          // Count total AG occurrences scheduled in current month of current year across all dates
          const agThisMonth = rows.reduce((sum, r) => {
            const allDates: string[] = Array.isArray(r.meetingDates) && r.meetingDates.length > 0
              ? r.meetingDates
              : (r.plannedAg ? [r.plannedAg] : []);
            const count = allDates.filter((iso) => {
              const d = new Date(iso);
              return !isNaN(d.getTime()) && d.getMonth() === currentMonth && d.getFullYear() === currentYear;
            }).length;
            return sum + count;
          }, 0);
          const notQualifiedWithForecast = rows.filter((r) => r.qualification === 'not-qualified' && !!r.nextAgForecast && r.nextAgForecast !== 'Prevision impossible').length;
          return (
            <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
              {status === 'PENDING' ? (
                <>
                  <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm"><Skeleton width={60} height={32} baseColor="#f1f5f9" highlightColor={`${tailwindConfig.theme.extend.colors['rgpd-blue']}45`} className="mb-2" /><Skeleton width={180} height={16} baseColor="#f1f5f9" highlightColor={`${tailwindConfig.theme.extend.colors['rgpd-blue']}45`} /></div>
                  <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm"><Skeleton width={60} height={32} baseColor="#f1f5f9" highlightColor={`${tailwindConfig.theme.extend.colors['rgpd-blue']}45`} className="mb-2" /><Skeleton width={180} height={16} baseColor="#f1f5f9" highlightColor={`${tailwindConfig.theme.extend.colors['rgpd-blue']}45`} /></div>
                  <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm"><Skeleton width={60} height={32} baseColor="#f1f5f9" highlightColor={`${tailwindConfig.theme.extend.colors['rgpd-blue']}45`} className="mb-2" /><Skeleton width={120} height={16} baseColor="#f1f5f9" highlightColor={`${tailwindConfig.theme.extend.colors['rgpd-blue']}45`} /></div>
                </>
              ) : (
                <>
                  <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                    <div className="text-2xl font-bold text-slate-900">{totalCopros}</div>
                    <div className="text-xs tracking-wide text-slate-500 uppercase">COPROPRIÉTÉS GÉRÉES</div>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                    <div className="text-2xl font-bold text-slate-900">{notQualifiedWithForecast}</div>
                    <div className="text-xs tracking-wide text-slate-500 uppercase">AG NON QUALIFIÉES AVEC PROCHAINES DATES</div>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                    <div className="text-2xl font-bold text-slate-900">{agThisMonth}</div>
                    <div className="text-xs tracking-wide text-slate-500 uppercase">
                      AG CE MOIS <span className="normal-case">({monthLabel})</span>
                    </div>
                  </div>
                </>
              )}
            </div>
          );
        })()}
        {layoutFilters}
        {status === 'PENDING' && (
          <div className="w-full bg-white pb-2 text-center">
            <Skeleton count={6} height={45} highlightColor={`${tailwindConfig.theme.extend.colors['rgpd-blue']}45`} className="my-3" />
          </div>
        )}
        {status === 'ERROR' && (
          <div className="my-10 text-center"><Badge mode="error" text={errorMsg || 'Erreur'} /></div>
        )}
        {status === 'SUCCESS' && (
          <div className="main-section">
            <h2 className="section-title text-lg font-semibold text-slate-900">Mes Copropriétés et leurs AG</h2>
            <div className="mb-2 flex justify-end">
              <button
                className="rounded-md bg-blue-600 px-3 py-2 text-xs text-white hover:bg-blue-700"
                onClick={() => setInfoModalOpen(true)}
              >
                <span role="img" aria-label="info">ℹ️</span> Comment sont évaluées les prévisions d'AG ?
              </button>
            </div>
            {filtered.length > 0 ? (
              <MUITable
                data={tableData}
                headers={headers}
                sortableColumns={['NB LOTS', 'DERNIERE AG', 'PREVISION PROCHAINE AG', 'DATE AG PRÉVUE']}
                disablePagination={false}
              />
            ) : (
              <div className="my-10 text-center">
                <Badge mode="warning" text="Aucune copropriété trouvée" />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Info Modal for Prevision AG */}
      <Popup isOpen={infoModalOpen} onClose={() => setInfoModalOpen(false)} title="ℹ️ Information sur la Prévision Prochaine AG">
        <div className="line-height-6 space-y-4 text-left text-gray-700">
          <div className="rounded border-l-4 border-red-500 bg-red-50 p-3">
            <p className="mb-2 font-semibold text-red-800">
              <span role="img" aria-label="warning">⚠️</span> ATTENTION : Cette prévision est uniquement indicative
            </p>
          </div>
          
          <div className="space-y-3">
            <p>
              • Le <strong>mois prévisionnel</strong> est calculé automatiquement par rapport à la dernière date d'AG saisie.
            </p>
            
            <p>
              • <strong>Si la dernière AG ne s'est pas tenue dans les 6 mois de la clôture des comptes</strong>, alors la date prévisionnelle affichée est <span className="font-semibold text-red-600">FAUSSE</span>.
            </p>
            
            <p>
              • Ce mois prévisionnel <strong>n'engage en rien</strong> sur quoi que ce soit et ne constitue pas une obligation légale.
            </p>
            
            <div className="rounded border-l-4 border-yellow-500 bg-yellow-50 p-3">
              <p className="mb-2 font-semibold text-yellow-800">Il est impératif que le gestionnaire remplisse correctement :</p>
              <ul className="list-inside list-disc space-y-1 text-yellow-700">
                <li><span role="img" aria-label="check">✓</span> Les bonnes dates des anciennes AG</li>
                <li><span role="img" aria-label="check">✓</span> Les nouvelles dates d'AG prévues</li>
                <li><span role="img" aria-label="check">✓</span> En tenant compte des règles légales en vigueur</li>
              </ul>
            </div>
          </div>
          
          <div className="mt-6 flex justify-center">
            <button 
              className="rounded-md bg-blue-600 px-6 py-2 text-white hover:bg-blue-700"
              onClick={() => setInfoModalOpen(false)}
            >
              J'ai compris
            </button>
          </div>
        </div>
      </Popup>
      </LocalizationProvider>
    </div>
  );
}

export default GeneralAssemblyManager;


